import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, User, Loader2 } from 'lucide-react';

export default function EditFanPlayerModal({ open, onOpenChange, player, onSuccess }) {
  const [name, setName] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [teamName, setTeamName] = useState('');
  const [season, setSeason] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoPublicId, setPhotoPublicId] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const fileInputRef = useRef(null);

  // Social fields
  const [instagram, setInstagram] = useState('');
  const [twitter, setTwitter] = useState('');
  const [highlightUrl, setHighlightUrl] = useState('');

  useEffect(() => {
    if (player && open) {
      setName(player.name || '');
      setJerseyNumber(player.jersey_number || '');
      setTeamName(player.team_name || '');
      setSeason(player.season || '');
      setPhotoUrl(player.profile_photo_url || '');
      setPhotoPublicId(player.profile_photo_public_id || '');
      setInstagram(player.instagram || '');
      setTwitter(player.twitter || '');
      setHighlightUrl(player.highlight_url || '');
      setActiveTab('profile');
    }
  }, [player, open]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be under 2MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP');
      return;
    }

    setUploading(true);
    try {
      const sigRes = await api.get('/cloudinary/signature', { params: { folder: 'fan_players' } });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'fan_players');

      const uploadRes = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, {
        method: 'POST',
        body: formData,
      });
      const result = await uploadRes.json();

      if (result.secure_url) {
        setPhotoUrl(result.secure_url);
        setPhotoPublicId(result.public_id);
        toast.success('Photo uploaded');
      }
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Player name is required');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: name.trim(),
        jersey_number: jerseyNumber.trim(),
        team_name: teamName.trim(),
        season: season.trim(),
      };
      if (photoUrl) updateData.profile_photo_url = photoUrl;
      if (photoPublicId) updateData.profile_photo_public_id = photoPublicId;
      if (instagram !== (player?.instagram || '')) updateData.instagram = instagram.trim();
      if (twitter !== (player?.twitter || '')) updateData.twitter = twitter.trim();
      if (highlightUrl !== (player?.highlight_url || '')) updateData.highlight_url = highlightUrl.trim();

      const res = await api.patch(`/fan/players/${player.player_id}`, updateData);
      toast.success('Player profile updated');
      onOpenChange(false);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-fan-player-modal" className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">Edit Profile</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile">Profile Details</TabsTrigger>
            <TabsTrigger value="socials">Socials & Highlights</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <div className="space-y-4 py-4">
              {/* Profile Photo */}
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Profile Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-slate-200">
                    {photoUrl ? (
                      <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-player-photo-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoUpload} />
                </div>
                <p className="text-xs text-slate-500 mt-1">JPEG, PNG, or WebP. Max 2MB.</p>
              </div>

              {/* Player Name */}
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Player Name *</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Player name"
                  className="h-12"
                  data-testid="edit-player-name-input"
                />
              </div>

              {/* Jersey Number */}
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Jersey Number</Label>
                <Input
                  value={jerseyNumber}
                  onChange={(e) => setJerseyNumber(e.target.value)}
                  placeholder="e.g. 3"
                  className="h-12"
                  data-testid="edit-player-jersey-input"
                />
              </div>

              {/* Team Name */}
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Team Name</Label>
                <Input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g. iChi Dragons"
                  className="h-12"
                  data-testid="edit-player-team-input"
                />
              </div>

              {/* Season */}
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Season</Label>
                <Input
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  placeholder="e.g. Spring 2026"
                  className="h-12"
                  data-testid="edit-player-season-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="save-player-profile-btn"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="socials">
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Instagram</Label>
                <Input
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="@username"
                  className="h-12"
                  data-testid="edit-player-instagram"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Twitter / X</Label>
                <Input
                  value={twitter}
                  onChange={(e) => setTwitter(e.target.value)}
                  placeholder="@username"
                  className="h-12"
                  data-testid="edit-player-twitter"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">Highlight Reel URL</Label>
                <Input
                  value={highlightUrl}
                  onChange={(e) => setHighlightUrl(e.target.value)}
                  placeholder="https://youtube.com/..."
                  className="h-12"
                  data-testid="edit-player-highlight-url"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="save-player-socials-btn"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
